import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography
} from "@mui/material"
import { useMemo } from "react"

import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import { ChangelogCategory, ChangelogStatus } from "@models/changelog"

const CORAL = "#E86A5C"

function timeAgo(
  value?: string | null,
  translate?: (k: string) => string
): string {
  if (!value) {
    return ""
  }
  const diff = Date.now() - new Date(value).getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  if (days <= 0) {
    return translate ? translate("changelog_today") : "today"
  }
  if (days < 7) {
    return `${days} ${translate ? translate("changelog_days_ago") : "days ago"}`
  }
  const weeks = Math.floor(days / 7)
  return `${weeks} ${translate ? translate("changelog_weeks_ago") : "weeks ago"}`
}

export default function PreviewDrawer() {
  const { translate, isPreviewOpen, togglePreview, announcements, categories } =
    useChangelogPageContext()

  const categoryMap = useMemo(() => {
    const map = new Map<string, ChangelogCategory>()
    categories.forEach((c) => map.set(c._id, c))
    return map
  }, [categories])

  const published = announcements.filter(
    (a) => a.status === ChangelogStatus.PUBLISHED
  )

  return (
    <Drawer
      anchor="right"
      open={isPreviewOpen}
      onClose={togglePreview}
      PaperProps={{ sx: { width: ["100vw", "27rem"], maxWidth: "27rem" } }}
    >
      <Stack sx={{ height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, background: CORAL, color: "white" }}
        >
          <Typography sx={{ fontWeight: 700 }}>
            {translate("changelog_tooltip").toLowerCase()}
          </Typography>
          <Stack direction="row" gap={0.5}>
            <IconButton size="small" sx={{ color: "white" }}>
              <SearchIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: "white" }}
              onClick={togglePreview}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, overflow: "auto", background: "white" }}>
          {published.length === 0 ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <Typography sx={{ color: "text.secondary" }}>
                {translate("changelog_empty_announcements")}
              </Typography>
            </Stack>
          ) : (
            published.map((a) => {
              const category = categoryMap.get(a.categoryId)
              const catName = category
                ? category.translations?.tr?.name ||
                  Object.values(category.translations || {})[0]?.name
                : undefined
              const t =
                a.translations?.tr || Object.values(a.translations || {})[0]
              const contentPreview = (t?.content || "")
                .replace(/<[^>]*>/g, " ")
                .replace(/\s+/g, " ")
                .trim()
              return (
                <Box key={a._id}>
                  <Box sx={{ px: 2.5, py: 2 }}>
                    {a.coverImageUrl && (
                      <Box
                        component="img"
                        src={a.coverImageUrl}
                        alt={t?.title || ""}
                        sx={{
                          width: "100%",
                          maxHeight: "12rem",
                          borderRadius: 1,
                          mb: 1,
                          objectFit: "cover"
                        }}
                      />
                    )}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      {catName && (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            color: category?.color || "text.secondary"
                          }}
                        >
                          {catName}
                        </Typography>
                      )}
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                      >
                        {timeAgo(a.publishedAt || a.createdAt, translate)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>
                      {t?.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        color: "text.secondary",
                        mt: 0.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}
                    >
                      {contentPreview}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        color: "text.secondary",
                        mt: 1,
                        cursor: "pointer"
                      }}
                    >
                      {translate("changelog_read_all")} ›
                    </Typography>
                    <Stack
                      direction="row"
                      gap={0.75}
                      sx={{ mt: 1.5, fontSize: "1.2rem" }}
                    >
                      <span>🙂</span>
                      <span>😐</span>
                      <span>😍</span>
                    </Stack>
                  </Box>
                  <Divider />
                </Box>
              )
            })
          )}
        </Box>
      </Stack>
    </Drawer>
  )
}
